import { useClientFetch } from "../hooks/useClientFetch.js";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing.js";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams.js";
import React, { Profiler, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearch } from "../store/search/selectors.js";
import { actions as LoaderActions } from "../store/loadingScreen/actions.js";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions.js";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions.js";
import { actions as brandedFaresActions } from "../store/brandedFareOffers/actions.js";
import { Route, useLocation } from "react-router-dom";
import useRouteName from "../hooks/useRouteName.js";
import StaticContext from "../app/StaticContext/StaticContext.js";
import useClientMetrics from "../hooks/useClientMetrics.js";
import { PageHeader } from "./Search.js";
import { AppShellDesktop } from "../components/elements/AppShell/index.js";
import Container from "../components/elements/Container/index.js";
import { Box, HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchbox from "../hooks/useSearchbox.js";
import useLocaleContext from "../hooks/useLocaleContext.js";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import SearchResultsOutboundDesktop from "../components/elements/SearchResults/SearchResults.outbound.desktop.js";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl.js";
import { goal, trackWithDefaultStage } from "@flights/et-universal";
import useFareCalendarEligible from "../hooks/useFareCalendarEligible.js";
import { getSearchCriteria } from "../store/getSearchCriteria.js";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen.js";
import { getSearchFetchStatus } from "../store/searchResultsBounds/outboundSelectors.js";
import { FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH } from "@flights/constants";
import { HomeDesktop, FlightDetailsDesktop, SearchInboundDesktop } from "../app/routes.js";
import { trackMetaChannelStagesV2 } from "../utils/experiments/track-meta-landings-v2.js";
import { useFlightSearchResultsGTMTag } from "../hooks/useGTMFlightTags.js";
import useTrackPageLoad from "../hooks/useTrackPageLoad.js";
import useLandingPageOfferPriceAccuracyEvent from "../hooks/c360/useLandingPageOfferPriceAccuracyEvent.js";
import { getVisible } from "../store/loadingScreen/selectors.js";
import { getMarketingTrackingVariable, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { useSpringDeal } from "../hooks/useSpringDeal.js";
import { useSummerDeal } from "../hooks/useSummerDeal.js";
import useGlobalContext from "../hooks/useGlobalContext.js";
import useProfiler from "../hooks/useProfiler.js";
import { WishlistWidgetLoader } from "../components/remote/index.js";
import { trackSplitBoundsOutboundSameOffer } from "../utils/experiments/funnel/flights_split_bounds_us.js";
const SearchOutboundDesktop = ({ getInitialState }) => {
    const search = useSelector(getSearch);
    const fetchStatus = useSelector(getSearchFetchStatus);
    const loadingScreenVisible = useSelector(getVisible);
    const dispatch = useDispatch();
    const location = useLocation();
    const routeName = useRouteName();
    const staticContext = useContext(StaticContext);
    const i18n = useI18n();
    const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState("");
    const { trackRenderTime } = useClientMetrics();
    const fireByeahSearchResultsTag = useFlightSearchResultsGTMTag();
    const trackSRContextEventV2 = useTrackPageLoad("search_results_outbound");
    const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
    const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
    const trackLandingPageOfferPriceAccuracyEvent = useLandingPageOfferPriceAccuracyEvent();
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { currencyCode, features } = useGlobalContext();
    const { isLoggedInAuthLow } = useProfiler();
    const isFareCalendarEligible = useFareCalendarEligible();
    const queryParams = new URLSearchParams(location.search);
    if (getInitialState && staticContext.set) {
        staticContext.set({
            getInitialState: getInitialState(queryParams, { selectedBound: "outbound" })
        });
    }
    useRemoveExternalParamsFromUrl();
    useEffect(() => {
        trackMetaChannelStagesV2("desktop");
        goal("flights_search_results_page_view");
        goal("tbu_vertical_search_results_view");
    }, []);
    const searchAPIParams = useSearchAPIParams({ outboundOnly: true });
    const url = `/api/flights/?${searchAPIParams}`;
    const isMetaOrigin = getMarketingTrackingVariable("ext-origin");
    useClientFetch(url, {
        delayFetchBy: !!isMetaOrigin && routeName === "flightdetails" ? 500 : 0,
        condition: () => {
            if (isSearchStoreInitial(search)) {
                return false;
            }
            return true;
        },
        fetch: () => {
            if (!location?.state?.hideLoader && !routeName.includes("searchresults")) {
                history.replaceState({ hideLoader: false }, document.title);
                dispatch(LoaderActions.show());
                setTimeout(() => {
                    dispatch(LoaderActions.hide());
                }, 6000);
            }
            dispatch(SearchResultsBoundsActions.fetchOutbound());
        },
        success: (data) => {
            dispatch(SearchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, search)));
            trackSplitBoundsOutboundSameOffer(data.flightOffers);
            data.flightOffers.forEach((offer) => {
                if (offer && !offer.requestableBrandedFares && offer.brandedFareOffers) {
                    dispatch(brandedFaresActions.fetchSuccess(offer.brandedFareOffers, offer.token));
                }
            });
            dispatch(SearchResultsBoundsActions.fetchSuccessOutbound({
                ...data,
                flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[0]] }))
            }));
            trackLandingPageOfferPriceAccuracyEvent(data, location.search);
            fireByeahSearchResultsTag();
            dispatch(LoaderActions.hide());
        },
        error: (data) => {
            goal("flights_search_results_loading_error");
            dispatch(LoaderActions.hide());
            dispatch(SearchResultsBoundsActions.fetchErrorOutbound(data));
        },
        forceRefetch: location?.state?.forceRefetch
    });
    useEffect(() => {
        if (["initial", "loading"].includes(fetchStatus)) {
            if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
                trackSRModalContextEventV2();
                setLastTrackedPageLoad("search_results_modal");
            }
            else if (lastTrackedPageLoad !== "search_results_skeleton") {
                trackSRSkeletonContextEventV2();
                setLastTrackedPageLoad("search_results_skeleton");
            }
        }
        else if (lastTrackedPageLoad !== "search_results_outbound") {
            trackSRContextEventV2();
            setLastTrackedPageLoad("search_results_outbound");
        }
    }, [fetchStatus]);
    const searchBoxProps = useSearchbox();
    const { isRTL } = useLocaleContext();
    const searchBoxControllerProps = { ...searchBoxProps, i18n, defaultSegmentLength: search.searchSegments.length };
    const [preloadDetails, toggle] = useState(false);
    if (!preloadDetails && fetchStatus === "success") {
        toggle(true);
        SearchInboundDesktop.preload();
        HomeDesktop.preload();
    }
    if (useReloadIfSearchStoreMissing(search)) {
        return null;
    }
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(PageHeader, { segments: search.searchSegments }),
        React.createElement(AppShellDesktop, null,
            React.createElement(Stack, { grow: true, gap: 6 },
                React.createElement(Box, { padding: 0, backgroundColor: "neutral_alt", mixin: { paddingBlockStart: 6, paddingBlockEnd: 6 } },
                    React.createElement(Container, null,
                        React.createElement(HiddenVisually, null,
                            React.createElement(Text, { tagName: "h1" }, i18n.trans(t("a11y_flights_sr_search_hidden_heading")))),
                        React.createElement(Stack, { gap: 2 },
                            React.createElement(SearchboxController, { ...searchBoxControllerProps, isRTL: isRTL, isResponsiveFixes: true, enabledCountriesAndAnywhere: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                                    isFareCalendarDesktopEnabled: isFareCalendarEligible &&
                                        (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                                            !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                                    isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                                    currencyCode
                                }, isCalendarReflowFixEnabled: isFeatureRunningClientSide("FLIGHTS_WEB_CAT_CALENDAR_FIX"), showCabinClassLabel: !!trackWithDefaultStage("flights_cat_cabin_class_label", 1) }, () => React.createElement(SearchBoxHorizontalDefault, null))))),
                React.createElement(Container, null,
                    React.createElement(SearchResultsOutboundDesktop, null)),
                React.createElement(Route, { key: FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH, path: FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH, exact: true },
                    React.createElement(FlightDetailsDesktop, null)),
                routeName.includes("searchresults") && React.createElement(SearchLoadingScreen, null),
                features["FLIGHTS_WEB_WISHLIST_WIDGET_DESKTOP_SSR"] && isLoggedInAuthLow() ? (React.createElement(WishlistWidgetLoader, null)) : undefined))));
};
export default React.memo(SearchOutboundDesktop);
